export interface GameweekNavigationRow {
  id?: string
  gameweek_number: number
  deadline: string
  effective_deadline?: string | null
  effective_state?: 'open' | 'schedule_pending' | 'locking' | 'locked' | 'held'
  editing_allowed?: boolean
  start_date?: string | null
  end_date?: string | null
}

export type GameweekNavigationStatus = 'past' | 'current' | 'future'

/** Results navigation follows effective deadlines and server-owned lock state. */
export function resolveAccessibleDashboardGameweeks<T extends GameweekNavigationRow>(
  rows: T[],
  now: Date = new Date()
): T[] {
  if (!Number.isFinite(now.getTime())) return []
  const validRows = [...rows]
    .filter((row) => getTimestamp(row.effective_deadline ?? row.deadline) != null)
    .sort((a, b) => a.gameweek_number - b.gameweek_number)
  const current = resolveDisplayGameweek(validRows, now)
  return current ? validRows.filter((row) => row.gameweek_number <= current.gameweek_number) : []
}
export function resolveDashboardGameweek(
  rows: GameweekNavigationRow[],
  now: Date = new Date()
): GameweekNavigationRow | null {
  return resolveAccessibleDashboardGameweeks(rows, now).at(-1) ?? null
}

function sortGameweeks(rows: GameweekNavigationRow[]): GameweekNavigationRow[] {
  return [...rows].sort((left, right) => left.gameweek_number - right.gameweek_number)
}

function getTimestamp(value: string | null | undefined): number | null {
  if (!value) return null

  const timestamp = new Date(value).getTime()
  return Number.isFinite(timestamp) ? timestamp : null
}

export function resolveDisplayGameweek(
  rows: GameweekNavigationRow[],
  now: Date = new Date()
): GameweekNavigationRow | null {
  const sortedRows = sortGameweeks(rows)
  if (sortedRows.length === 0) return null

  const nowTime = now.getTime()
  // A week remains the displayed current week until the next deadline. An
  // early fixture can make its effective lock current before the published
  // deadline; the database supplies that state independently of browser time.
  const passedWeeks = sortedRows.filter((row) => {
    if (
      row.effective_state === 'locking' ||
      row.effective_state === 'locked' ||
      row.effective_state === 'held'
    )
      return true
    const deadlineTime = getTimestamp(row.effective_deadline ?? row.deadline)
    return deadlineTime != null && deadlineTime <= nowTime
  })
  return passedWeeks[passedWeeks.length - 1] ?? sortedRows[0] ?? null
}

export function resolveGameweekStatus(
  row: GameweekNavigationRow,
  displayGameweekNumber: number | null
): GameweekNavigationStatus {
  if (displayGameweekNumber == null) {
    return 'future'
  }

  if (row.gameweek_number < displayGameweekNumber) {
    return 'past'
  }

  if (row.gameweek_number === displayGameweekNumber) {
    return 'current'
  }

  return 'future'
}

export function annotateGameweekStatuses(
  rows: GameweekNavigationRow[],
  now: Date = new Date()
): Array<GameweekNavigationRow & { status: GameweekNavigationStatus }> {
  const displayGameweek = resolveDisplayGameweek(rows, now)
  const displayGameweekNumber = displayGameweek?.gameweek_number ?? null

  return sortGameweeks(rows).map((row) => ({
    ...row,
    status: resolveGameweekStatus(row, displayGameweekNumber)
  }))
}

export function buildGameweekInfoFromRows(
  rows: GameweekNavigationRow[],
  season: string,
  now: Date = new Date()
): {
  currentGameweek: number
  deadline: string
  currentDeadline: string
  nextGameweekStart?: string
  season: string
  timeUntilDeadline: string
} {
  const sortedRows = sortGameweeks(rows)
  const displayGameweek = resolveDisplayGameweek(sortedRows, now)

  if (!displayGameweek) {
    return {
      currentGameweek: 0,
      season,
      deadline: now.toISOString(),
      currentDeadline: now.toISOString(),
      timeUntilDeadline: 'No active gameweek'
    }
  }

  const deadlineTime = new Date(displayGameweek.effective_deadline ?? displayGameweek.deadline)
  const timeUntil = deadlineTime.getTime() - now.getTime()

  let timeUntilDeadline = ''
  if (displayGameweek.effective_state === 'schedule_pending') {
    timeUntilDeadline = 'Schedule pending'
  } else if (displayGameweek.effective_state === 'held') {
    timeUntilDeadline = 'Schedule under review'
  } else if (timeUntil > 0) {
    const days = Math.floor(timeUntil / (1000 * 60 * 60 * 24))
    const hours = Math.floor((timeUntil % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    const minutes = Math.floor((timeUntil % (1000 * 60 * 60)) / (1000 * 60))

    if (days > 0) {
      timeUntilDeadline = `${days}d ${hours}h`
    } else if (hours > 0) {
      timeUntilDeadline = `${hours}h ${minutes}m`
    } else {
      timeUntilDeadline = `${minutes}m`
    }
  } else {
    timeUntilDeadline = 'Deadline passed'
  }

  const followingGameweek =
    sortedRows.find((row) => row.gameweek_number > displayGameweek.gameweek_number) ?? null

  return {
    currentGameweek: displayGameweek.gameweek_number,
    season,
    deadline: displayGameweek.effective_deadline ?? displayGameweek.deadline,
    currentDeadline: displayGameweek.effective_deadline ?? displayGameweek.deadline,
    nextGameweekStart: followingGameweek?.start_date ?? undefined,
    timeUntilDeadline
  }
}

export function resolveTrackedSelectedGameweek(params: {
  autoTrackedGameweek: number | null
  currentGameweek: number | null | undefined
  selectedGameweek: number
}): number {
  const currentGameweek = params.currentGameweek ?? 0
  if (currentGameweek <= 0) {
    return params.selectedGameweek
  }

  if (params.selectedGameweek <= 0 || params.selectedGameweek > currentGameweek) {
    return currentGameweek
  }

  if (
    params.autoTrackedGameweek != null &&
    params.selectedGameweek === params.autoTrackedGameweek
  ) {
    return currentGameweek
  }

  return params.selectedGameweek
}

export function resolveEditableSaveGameweek(
  rows: GameweekNavigationRow[],
  selectedGameweek: number | null | undefined,
  now: Date = new Date()
): number | null {
  void selectedGameweek
  const nowTime = now.getTime()
  return (
    sortGameweeks(rows).find((row) => {
      if (
        row.editing_allowed === false ||
        row.effective_state === 'locking' ||
        row.effective_state === 'locked' ||
        row.effective_state === 'held'
      )
        return false
      const deadlineTime = getTimestamp(row.effective_deadline ?? row.deadline)
      return deadlineTime != null && deadlineTime > nowTime
    })?.gameweek_number ?? null
  )
}
